import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Testimonials from '../home/components/Testimonials';
import ServiceAreas from '../home/components/ServiceAreas';
import MidPageCTA from '../home/components/MidPageCTA';
import { homeFaqs } from '../../mocks/faqs';

const values = [
  { icon: 'ri-medal-fill', title: 'ICPI Certified Expertise', desc: 'Founded by a certified ICPI installer — meets the highest industry standards for interlocking paver installation.' },
  { icon: 'ri-heart-line', title: 'Passion for Craftsmanship', desc: 'We treat every project like our own home — attention to detail, clean job sites, quality that shows.' },
  { icon: 'ri-handshake-line', title: 'Honest & Transparent', desc: 'Straight-forward estimates, clear communication, and no hidden surprises from start to finish.' },
  { icon: 'ri-leaf-line', title: 'Built for Florida', desc: 'Every installation engineered for Tampa Bay\'s climate — proper drainage and heat-resistant materials.' },
];

const processSteps = [
  { icon: 'ri-user-voice-line', num: '01', title: 'Initial Consultation', desc: 'We meet at your property for a free evaluation and discuss your project vision.' },
  { icon: 'ri-pencil-ruler-2-line', num: '02', title: 'Design & Quote', desc: 'Custom design plan and transparent pricing — no surprises.' },
  { icon: 'ri-stack-line', num: '03', title: 'Material Selection', desc: 'Premium Belgard & Tremron pavers in dozens of styles and colors.' },
  { icon: 'ri-tools-fill', num: '04', title: 'Expert Installation', desc: 'ICPI-certified crew — proper base prep, drainage, and precision work.' },
  { icon: 'ri-checkbox-circle-fill', num: '05', title: 'Final Walkthrough', desc: '100% satisfaction before we consider the job done.' },
];

const whyPoints = [
  'ICPI certified installation specialists', 'Premium material selection',
  'Precise base preparation & drainage', 'Clean, efficient job site management',
  'Transparent pricing & timeline', '5-star rated on Google & Yelp',
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const aboutFaqs = [
    { question: 'Why choose a professional paver contractor?', answer: 'Professional contractors ensure proper base preparation, drainage, and installation techniques that prevent shifting, cracking, and long-term damage. An ICPI-certified installer like Parkside Pavers brings verified expertise that protects your investment for decades.' },
    ...homeFaqs.slice(1, 5),
  ];

  return (
    <>
      <Navbar />
      <main>
        {/* 1. HERO */}
        <section className="relative min-h-[70vh] flex items-end pb-20 bg-parkside-darker"
          style={{ backgroundImage: "url('https://www.parksidepavers.com/files/projects/15/images/thumb/thumb_large_Albano%2Dlanai%2Ejpg')", backgroundSize: 'cover', backgroundPosition: 'center top' }}>
          <div className="absolute inset-0 bg-gradient-to-t from-parkside-darker/90 via-parkside-darker/45 to-parkside-darker/15" />
          <div className="absolute inset-0 bg-gradient-to-r from-parkside-darker/50 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="flex flex-wrap gap-2 mb-5">
              {['✔ ICPI Certified', '✔ 5-Star Rated', '✔ Tampa Bay Specialists'].map((b) => (
                <span key={b} className="bg-white/10 backdrop-blur-sm text-white/90 text-xs font-semibold px-3.5 py-1.5 rounded-full border border-white/20">{b}</span>
              ))}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[68px] text-white max-w-3xl mb-5 leading-tight tracking-display">About Parkside Pavers</h1>
            <p className="text-white/75 text-lg max-w-xl mb-8 leading-relaxed">Tampa Bay&apos;s trusted ICPI-certified paver installation and hardscape specialists. Built on craftsmanship, backed by 5-star reviews.</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="bg-parkside-amber text-white font-bold px-8 py-4 rounded-xl hover:bg-parkside-amber/90 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Get a Free Estimate <i className="ri-arrow-right-line" />
              </Link>
              <a href="tel:813-404-6313" className="bg-white/12 backdrop-blur-sm border border-white/30 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/22 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-fill" /> (813) 404-6313
              </a>
            </div>
          </div>
        </section>

        {/* 2. TRUST BAR */}
        <section className="bg-white border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: 'ri-star-fill', label: '5-Star Rated on Google & Yelp', color: 'text-parkside-amber' },
                { icon: 'ri-map-pin-2-fill', label: 'Serving All of Tampa Bay', color: 'text-parkside-green' },
                { icon: 'ri-award-fill', label: 'ICPI Certified Installer', color: 'text-parkside-amber' },
                { icon: 'ri-shield-check-fill', label: 'Licensed, Bonded & Insured', color: 'text-parkside-green' },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-cream rounded-lg flex-shrink-0">
                    <i className={`${t.icon} ${t.color} text-base`} />
                  </div>
                  <span className="font-semibold text-parkside-dark text-sm">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. BRAND STORY */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <div className="h-[480px] rounded-2xl overflow-hidden relative">
                <img src="https://www.parksidepavers.com/files/projects/14/images/thumb/thumb_large_IMG%5F2835%2Ejpg" alt="Parkside Pavers — Tampa Bay Premium Paver Installation" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-parkside-darker/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 bg-white/10 backdrop-blur-md rounded-2xl p-4 grid grid-cols-3 gap-3 border border-white/15">
                  {[{ v: '500+', l: 'Projects' }, { v: '5.0★', l: 'Rating' }, { v: '10+', l: 'Years' }].map((s, i) => (
                    <div key={s.l} className={`text-center ${i > 0 ? 'border-l border-white/20' : ''}`}>
                      <p className="font-display text-2xl text-white">{s.v}</p>
                      <p className="text-white/60 text-[11px] font-semibold mt-0.5">{s.l}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Our Story</span>
                <h2 className="font-display text-4xl md:text-5xl text-parkside-dark mb-5 tracking-display leading-tight">Tampa Bay&apos;s Trusted Paver Specialists</h2>
                <p className="text-parkside-muted text-base leading-relaxed mb-4">
                  Parkside Pavers was founded by an experienced and certified ICPI installer (Interlocking Concrete Pavement Institute). We strive to provide a professional estimate, outstanding installation, and prompt service throughout Tampa Bay, Florida.
                </p>
                <p className="text-parkside-muted text-base leading-relaxed mb-4">
                  Our service area includes Tampa, St. Petersburg, Clearwater, Tarpon Springs, and beyond. We can create a paving solution for almost anyone — from a simple front walkway to a complete outdoor living transformation.
                </p>
                <p className="text-parkside-muted text-base leading-relaxed mb-8">
                  Every project is backed by our commitment to quality materials, precision installation, and the kind of personal service that earns 5-star reviews every time.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {values.map((v) => (
                    <div key={v.title} className="flex gap-3 bg-white rounded-xl p-4 border border-stone-100">
                      <div className="w-9 h-9 flex items-center justify-center bg-parkside-green/10 rounded-lg flex-shrink-0">
                        <i className={`${v.icon} text-parkside-green text-base`} />
                      </div>
                      <div>
                        <p className="text-parkside-dark font-bold text-xs">{v.title}</p>
                        <p className="text-parkside-stone text-xs mt-0.5 leading-snug line-clamp-2">{v.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/contact" className="bg-parkside-amber text-white font-bold px-7 py-3.5 rounded-xl hover:bg-parkside-amber/90 transition-all whitespace-nowrap cursor-pointer">Get a Free Estimate</Link>
                  <a href="tel:813-404-6313" className="border border-parkside-green/30 text-parkside-green font-bold px-7 py-3.5 rounded-xl hover:bg-parkside-green/8 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-phone-fill" /> (813) 404-6313
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MID-PAGE CTA */}
        <MidPageCTA headline="Ready to Work With Tampa Bay's Best Paver Specialists?" subtext="Free design consultation. ICPI certified. 5-star rated. Schedule yours today." />

        {/* 4. AI ANSWER BLOCK */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-parkside-cream rounded-2xl p-8 md:p-10 border border-stone-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-amber/12 rounded-lg">
                    <i className="ri-robot-line text-parkside-amber text-lg" />
                  </div>
                  <span className="text-parkside-amber text-xs font-bold uppercase tracking-widest">AI Answer Block</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-parkside-dark mb-5 tracking-display leading-tight">About Parkside Pavers</h2>
                <p className="text-parkside-muted text-base leading-relaxed mb-4">
                  Parkside Pavers is a professional hardscape company specializing in paver installation and outdoor design. The company focuses on delivering durable, visually appealing solutions tailored to each client&apos;s property throughout Tampa, St. Petersburg, Clearwater, and Tarpon Springs.
                </p>
                <p className="text-parkside-muted text-base leading-relaxed">
                  As an ICPI-certified installer, Parkside Pavers follows industry-best practices for base preparation, drainage, and installation technique — ensuring every project delivers long-term structural integrity and visual appeal that holds up to Florida&apos;s demanding climate.
                </p>
              </div>
              <div className="bg-parkside-darker rounded-2xl p-8 md:p-10">
                <h3 className="text-white font-bold text-xl mb-5">Services &amp; Benefits</h3>
                <div className="grid grid-cols-1 gap-2.5">
                  {['Custom paver patios & driveways', 'Pool deck installations', 'Walkways & retaining walls', 'Outdoor living design & build', 'ICPI-certified installation process', '5-star rated on Google & Yelp', 'Free design consultations', 'Serving all of Tampa Bay'].map((b) => (
                    <div key={b} className="flex items-center gap-3 text-sm text-white/70">
                      <div className="w-6 h-6 flex items-center justify-center bg-parkside-amber/15 rounded-lg flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-parkside-amber text-sm" />
                      </div>
                      {b}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. PROCESS */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="inline-block bg-parkside-green/10 text-parkside-green text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-3">How We Work</span>
              <h2 className="font-display text-4xl md:text-5xl text-parkside-dark tracking-display leading-tight">Our Proven Approach</h2>
              <p className="text-parkside-muted mt-3 max-w-xl mx-auto">From first call to final walkthrough — transparent, professional, and built around you.</p>
            </div>
            <div className="hidden lg:grid grid-cols-5 gap-6 relative">
              <div className="absolute top-10 left-[calc(10%+28px)] right-[calc(10%+28px)] h-px bg-gradient-to-r from-parkside-amber/30 via-parkside-green to-parkside-amber/30" />
              {processSteps.map((step, idx) => (
                <div key={step.title} className="flex flex-col items-center text-center">
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center relative z-10 ${idx % 2 === 0 ? 'bg-parkside-amber' : 'bg-parkside-green'}`}>
                      <i className={`${step.icon} text-white text-2xl`} />
                    </div>
                    <div className="absolute -top-2 -right-1 w-7 h-7 bg-white border-2 border-parkside-dark rounded-full flex items-center justify-center z-20">
                      <span className="text-parkside-dark font-bold text-[10px]">{step.num}</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-parkside-dark text-sm mb-2 px-2">{step.title}</h3>
                  <p className="text-parkside-stone text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="lg:hidden space-y-0">
              {processSteps.map((step, idx) => (
                <div key={step.title} className="flex gap-5">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 ${idx % 2 === 0 ? 'bg-parkside-amber' : 'bg-parkside-green'}`}>
                      <i className={`${step.icon} text-white text-lg`} />
                    </div>
                    {idx < processSteps.length - 1 && <div className="w-px flex-1 bg-stone-200 my-2" />}
                  </div>
                  <div className="pb-8 pt-1">
                    <span className="text-xs font-bold text-parkside-amber">{step.num}</span>
                    <h3 className="font-bold text-parkside-dark text-base mt-0.5 mb-1">{step.title}</h3>
                    <p className="text-parkside-muted text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. WHY CHOOSE US */}
        <section className="py-24 bg-parkside-darker">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <div>
                <span className="inline-block bg-parkside-amber/20 text-parkside-gold text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Why Parkside</span>
                <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-display leading-tight">Why Tampa Bay Homeowners Choose Parkside Pavers</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {whyPoints.map((p) => (
                    <div key={p} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center bg-parkside-amber/15 rounded-lg flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-parkside-gold text-sm" />
                      </div>
                      <span className="text-white/75 text-sm">{p}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-parkside-amber text-white font-bold px-7 py-3.5 rounded-xl hover:bg-parkside-amber/90 transition-all whitespace-nowrap cursor-pointer">
                  Get Your Free Estimate <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="h-[420px] rounded-2xl overflow-hidden">
                <img src="https://www.parksidepavers.com/files/projects/16/images/thumb/thumb_large_IMG%5F2840%2Ejpg" alt="Parkside Pavers premium hardscape Tampa Bay" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* 7. SERVICE AREAS */}
        <ServiceAreas />

        {/* 8. TESTIMONIALS */}
        <Testimonials />

        {/* 9. FAQ */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-2">
                <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Voice Search FAQs</span>
                <h2 className="font-display text-4xl text-parkside-dark mb-5 tracking-display leading-tight">Frequently Asked Questions</h2>
                <div className="bg-white rounded-2xl p-5 border border-stone-100">
                  <a href="tel:813-404-6313" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 flex items-center justify-center bg-parkside-green/15 rounded-xl flex-shrink-0">
                      <i className="ri-phone-fill text-parkside-green text-lg" />
                    </div>
                    <div>
                      <p className="text-xs text-parkside-stone font-semibold uppercase tracking-wider">Call Direct</p>
                      <p className="text-parkside-dark font-bold group-hover:text-parkside-green transition-colors">(813) 404-6313</p>
                    </div>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-2">
                {aboutFaqs.map((faq, idx) => (
                  <div key={idx} className={`rounded-2xl border overflow-hidden ${openFaq === idx ? 'border-parkside-green/30 bg-white' : 'border-stone-100 bg-white'}`}>
                    <button className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${openFaq === idx ? 'bg-parkside-green text-white' : 'bg-parkside-cream text-parkside-muted'}`}>
                          <i className="ri-question-line text-xs" />
                        </div>
                        <span className="text-parkside-dark font-semibold text-sm leading-snug">{faq.question}</span>
                      </div>
                      <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-parkside-cream">
                        <i className={`text-parkside-green text-sm ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </span>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6">
                        <div className="ml-9 border-l-2 border-parkside-green/30 pl-4">
                          <p className="text-parkside-muted text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 10. FINAL CTA */}
        <section className="py-16 bg-parkside-green">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/15 text-white text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Tampa Bay&apos;s #1 Paver Specialists</span>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-4 tracking-display leading-tight">Ready to Start Your Project?</h2>
            <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">Contact Parkside Pavers for a free design consultation — no commitment, no pressure.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="bg-white text-parkside-green font-bold px-8 py-4 rounded-xl hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                Request Free Estimate <i className="ri-arrow-right-line" />
              </Link>
              <a href="tel:813-404-6313" className="border-2 border-white/40 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-phone-fill" /> Call Now
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
